import { useContext, useRef, useSyncExternalStore } from 'react'
import { throwPlatformError, UIErrorCode } from '../../errors'
import {
  PageStateContext,
  type ComponentState,
  type PageStateSnapshot,
} from '../context'

/**
 * Selector-style hook over `PageStateContext`. Re-renders the calling
 * component only when the selector's return value changes by `isEqual`
 * (defaults to `Object.is`). Use this in place of
 * `usePageStateContext().getAllComponents().find(...)` when you only
 * care about a slice of the page state — unrelated mutations on other
 * components' slots no longer trigger a re-render.
 *
 * Memoise the selector (or pass a stable reference) for best results;
 * an inline closure rebuilt on every render forces the hook to
 * re-evaluate every cycle even though `useSyncExternalStore` is doing
 * its job.
 *
 * @example
 *   const activeViewName = usePageStateSelector((snapshot) => {
 *     const raw = Object.values(snapshot).find(
 *       (c) => c.componentType === 'ListView',
 *     )?.state.activeViewName
 *     return typeof raw === 'string' ? raw : undefined
 *   })
 */
export function usePageStateSelector<T>(
  selector: (snapshot: PageStateSnapshot) => T,
  isEqual: (a: T, b: T) => boolean = Object.is,
): T {
  const context = useContext(PageStateContext)
  if (!context) {
    throwPlatformError(
      'Unable to find PageStateContext. Has PageStateProvider been initialized?',
      UIErrorCode.CORE_CONTEXT_NOT_FOUND,
    )
  }

  // Per-call cache that mirrors `useSyncExternalStoreWithSelector` from
  // the React shim package — implemented inline to avoid the extra
  // runtime dep. Returning the cached value when `isEqual` reports no
  // change lets React's bail-out skip the re-render entirely.
  const cacheRef = useRef<{ value: T; hasValue: boolean }>({
    value: undefined as unknown as T,
    hasValue: false,
  })

  return useSyncExternalStore(context.subscribe, () => {
    const next = selector(context.getSnapshot())
    if (cacheRef.current.hasValue && isEqual(cacheRef.current.value, next)) {
      return cacheRef.current.value
    }
    cacheRef.current = { value: next, hasValue: true }
    return next
  })
}

/**
 * Resolve a single component slot deterministically by `componentType`
 * and an optional `metadata.role` disambiguator. Returns the first
 * match or `undefined` when no slot is registered for the given pair.
 *
 * For single-instance pages, omit `role` and you'll get the only
 * component of that type. For multi-instance pages (e.g. tabs each
 * carrying their own `mortar.ListView`), set distinct `componentRole`
 * values on the producers and pass the matching `role` here.
 *
 * **First-match-wins.** When two slots share both `componentType` AND
 * `role`, the hook returns whichever appears first in the snapshot's
 * insertion order. Callers are expected to keep `(componentType, role)`
 * pairs unique on the same page. Currently every JS engine in scope
 * (V8, JSC, SpiderMonkey) preserves insertion order for string keys,
 * but this isn't guaranteed by the spec — pin uniqueness on the
 * producer side rather than relying on iteration order.
 *
 * Built on top of `usePageStateSelector`, so consumers only re-render
 * when the resolved slot's identity changes.
 *
 * @example
 *   const listSlot = useComponentByRole('ListView', 'workers')
 *   const raw = listSlot?.state.activeViewName
 *   const activeViewName = typeof raw === 'string' ? raw : undefined
 */
export function useComponentByRole(
  componentType: string,
  role?: string,
): ComponentState | undefined {
  return usePageStateSelector((snapshot) => {
    for (const componentId in snapshot) {
      const c = snapshot[componentId]
      if (c.componentType !== componentType) continue
      if (role !== undefined && c.metadata?.role !== role) continue
      return c
    }
    return undefined
  }, isComponentSlotEqual)
}

/**
 * Equality used by `useComponentByRole`. Two slots are equal when
 * their identity (`componentId`) AND their `state` reference match —
 * `mergeComponentState` / `setComponentState` always allocate a new
 * `state` object, so reference identity is a cheap proxy for content
 * change.
 */
const isComponentSlotEqual = (
  a: ComponentState | undefined,
  b: ComponentState | undefined,
): boolean => {
  if (a === b) return true
  if (!a || !b) return false
  return a.componentId === b.componentId && a.state === b.state
}
